import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react';
import { useNavigate, useRouteError } from 'react-router';

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'statusText' in error) return String(error.statusText);
  return String(error ?? 'Unknown error');
}

/**
 * The route-level error screen. Attached to every page route so the shell (the nav)
 * survives a page crash, and to the top-level routes as a full-screen catch-all.
 */
export function RouteErrorBoundary({ fullScreen = false }: { fullScreen?: boolean }) {
  const error = useRouteError();
  const navigate = useNavigate();
  return (
    <div
      data-testid="route-error"
      className={`flex items-center justify-center p-6 ${fullScreen ? 'min-h-screen bg-page-bg' : 'min-h-[60vh]'}`}
    >
      <div className="max-w-md w-full rounded-[10px] border border-card-border bg-card-bg py-12 px-8 text-center">
        <AlertTriangle size={48} strokeWidth={2} className="mx-auto mb-6 text-page-muted" />
        <h1 className="text-2xl font-semibold text-page-text mb-2">Something went wrong</h1>
        <p className="text-sm text-page-muted mb-6 break-words">{messageOf(error)}</p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowLeft size={20} strokeWidth={2.5} />
            Go Back
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer border-0 hover:bg-nav-active-hover transition-colors"
          >
            <RefreshCw size={20} strokeWidth={2.5} />
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}
